import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import PayrollHeader from './PayrollHeader'
import PayrollTabs from './PayrollTabs'
import './UploadSalaryExcel.css'

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const now = new Date()

// Current month plus the previous 5 months, oldest first - e.g. for August:
// [March, April, May, June, July, August]. Previous month (index length-2)
// is the default.
const AVAILABLE_MONTHS = Array.from({ length: 6 }, (_, i) => {
  const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1)
  return { month: d.getMonth() + 1, year: d.getFullYear(), label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` }
})
const DEFAULT_MONTH_OPTION = AVAILABLE_MONTHS[AVAILABLE_MONTHS.length - 2]

function UploadSalaryExcel() {
  const navigate = useNavigate()
  const [month, setMonth] = useState(DEFAULT_MONTH_OPTION.month)
  const [year, setYear] = useState(DEFAULT_MONTH_OPTION.year)
  const [groups, setGroups] = useState([])
  const [selectedGroups, setSelectedGroups] = useState([])
  const [isGenerating, setIsGenerating] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [detailList, setDetailList] = useState([])
  const [reportRows, setReportRows] = useState(null)
  // True once a Generate attempt has completed (success, warnings, or error).
  // Gates the "View Salary Statement" button - it's hidden before the first
  // Generate, then always shown afterwards regardless of any issues.
  const [hasGenerated, setHasGenerated] = useState(false)
  const [unmatchedEmployees, setUnmatchedEmployees] = useState([])
  const [zeroAttendanceEmployees, setZeroAttendanceEmployees] = useState([])
  const [missingBankAccountEmployees, setMissingBankAccountEmployees] = useState([])
  const [reviewRequiredEmployees, setReviewRequiredEmployees] = useState([])
  const [advanceWarnings, setAdvanceWarnings] = useState([])
  const [payNotReady, setPayNotReady] = useState([])
  const [advanceForNonPayable, setAdvanceForNonPayable] = useState([])
  const [advanceMissingFromReport, setAdvanceMissingFromReport] = useState([])
  const [advanceExceedsSalary, setAdvanceExceedsSalary] = useState([])

  useEffect(() => {
    api.get('/payroll/groups')
      .then((response) => {
        setGroups(response.data.groups || [])
      })
      .catch(() => setGroups([]))
  }, [])

  function resetMessages() {
    setSuccessMessage('')
    setErrorMessage('')
    setDetailList([])
    setReportRows(null)
    setHasGenerated(false)
    setUnmatchedEmployees([])
    setZeroAttendanceEmployees([])
    setMissingBankAccountEmployees([])
    setReviewRequiredEmployees([])
    setAdvanceWarnings([])
    setPayNotReady([])
    setAdvanceForNonPayable([])
    setAdvanceMissingFromReport([])
    setAdvanceExceedsSalary([])
  }

  function toggleGroup(group) {
    setSelectedGroups((prev) =>
      prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group]
    )
  }

  async function handleGenerate() {
    resetMessages()

    setIsGenerating(true)
    try {
      const response = await api.get('/payroll/generate-from-attendance', {
        params: { month, year, groups: JSON.stringify(selectedGroups) },
      })

      setSuccessMessage(response.data.message)
      setReportRows(response.data.data)
      setUnmatchedEmployees(response.data.unmatched_employees || [])
      setZeroAttendanceEmployees(response.data.zero_attendance_employees || [])
      setMissingBankAccountEmployees(response.data.missing_bank_account_employees || [])
      setReviewRequiredEmployees(response.data.review_required_employees || [])
      setAdvanceWarnings(response.data.advance_warnings || [])
      setPayNotReady(response.data.pay_not_ready || [])
      setAdvanceForNonPayable(response.data.advance_for_non_payable_warnings || [])
      setAdvanceMissingFromReport(response.data.advance_missing_from_report_warnings || [])
      setAdvanceExceedsSalary(response.data.advance_exceeds_salary_warnings || [])
    } catch (error) {
      const detail = error.response?.data?.detail
      if (detail && typeof detail === 'object' && detail.errors) {
        setErrorMessage(detail.message || 'Validation failed.')
        setDetailList(detail.errors)
      } else if (detail && typeof detail === 'object' && detail.unmatched_employees) {
        setErrorMessage(detail.message || 'Some employees were not found.')
        setDetailList(detail.unmatched_employees)
      } else if (detail && typeof detail === 'object' && detail.already_confirmed) {
        setErrorMessage(detail.message || 'Some employees are already confirmed.')
        setDetailList(detail.already_confirmed)
      } else if (detail) {
        setErrorMessage(typeof detail === 'string' ? detail : 'Upload failed. Please try again.')
      } else {
        setErrorMessage('Could not reach the server. Is the backend running?')
      }
    } finally {
      setIsGenerating(false)
      setHasGenerated(true)
    }
  }

  return (
    <>
      <PayrollHeader />
      <PayrollTabs />
      <div className="upload-page">
      <div className="upload-card">
        <h1>Generate Payroll</h1>
        <p className="subtitle">Generate Payroll details for the below departments</p>

        <div className="month-year-row">
          <div style={{ flex: 1 }}>
            <label htmlFor="payroll-month">Month</label>
            <select
              id="payroll-month"
              value={`${month}-${year}`}
              onChange={(e) => {
                const [m, y] = e.target.value.split('-').map(Number)
                setMonth(m)
                setYear(y)
                resetMessages()
              }}
            >
              {AVAILABLE_MONTHS.map((opt) => (
                <option key={`${opt.month}-${opt.year}`} value={`${opt.month}-${opt.year}`}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {groups.length > 0 && (
          <div className="department-filter">
            <label className="department-filter-label">Filter by Pay Group (optional)</label>
            <div className="department-group-box">
              {groups.map((group) => (
                <label key={group} className="department-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedGroups.includes(group)}
                    onChange={() => toggleGroup(group)}
                  />
                  {group}
                </label>
              ))}
            </div>
            <p className="department-hint">
              {selectedGroups.length === 0
                ? 'No pay groups selected - all pay groups will be included.'
                : `Only showing: ${selectedGroups.join(', ')}`}
            </p>
          </div>
        )}

        <button onClick={handleGenerate} disabled={isGenerating}>
          {isGenerating ? 'Generating...' : 'Generate'}
        </button>

        {successMessage && <p className="success-message">{successMessage}</p>}
        {advanceWarnings.length > 0 && (
          <div className="row-errors" style={{ background: '#fff8e1', borderColor: '#f0c14b' }}>
            <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
              These employees have advance data entered in both Attendance and Salary Advance - please verify
              before proceeding (both amounts are added together):
            </p>
            <ul>
              {advanceWarnings.map((name, index) => (
                <li key={index}>{name}</li>
              ))}
            </ul>
          </div>
        )}
        {payNotReady.length > 0 && (
          <div className="row-errors" style={{ background: '#fff8e1', borderColor: '#f0c14b' }}>
            <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
              These employees are marked Prepare Payroll Data but pay isn't fully ready - please verify before
              proceeding:
            </p>
            <ul>
              {payNotReady.map((item, index) => (
                <li key={index}>
                  {item.employee}{item.department ? ` (${item.department})` : ''}: {item.reasons.join('; ')}
                </li>
              ))}
            </ul>
          </div>
        )}
        {advanceForNonPayable.length > 0 && (
          <div className="row-errors" style={{ background: '#fff8e1', borderColor: '#f0c14b' }}>
            <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
              These employees have Salary Advance data entered, but "Prepare Payroll Data" is off for them - they
              won't appear in this payroll run, please verify:
            </p>
            <ul>
              {advanceForNonPayable.map((name, index) => (
                <li key={index}>{name}</li>
              ))}
            </ul>
          </div>
        )}
        {advanceMissingFromReport.length > 0 && (
          <div className="row-errors" style={{ background: '#fff8e1', borderColor: '#f0c14b' }}>
            <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
              These employees have a Salary Advance, but don't appear anywhere in this pay run - their advance
              can't be applied, please verify:
            </p>
            <ul>
              {advanceMissingFromReport.map((entry, index) => (
                <li key={index}>{entry}</li>
              ))}
            </ul>
          </div>
        )}
        {advanceExceedsSalary.length > 0 && (
          <div className="row-errors" style={{ background: '#fff8e1', borderColor: '#f0c14b' }}>
            <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
              These employees' advance deduction is more than what they earned this month - please verify before
              proceeding:
            </p>
            <ul>
              {advanceExceedsSalary.map((name, index) => (
                <li key={index}>{name}</li>
              ))}
            </ul>
          </div>
        )}
        {unmatchedEmployees.length > 0 && (
          <div className="row-errors" style={{ background: '#fff8e1', borderColor: '#f0c14b' }}>
            <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
              These Employee/Department combinations were not found among active employees - they're left out
              of this pay run, everyone else is still included. Please verify:
            </p>
            <ul>
              {unmatchedEmployees.map((entry, index) => (
                <li key={index}>{entry}</li>
              ))}
            </ul>
          </div>
        )}
        {zeroAttendanceEmployees.length > 0 && (
          <div className="row-errors" style={{ background: '#fff8e1', borderColor: '#f0c14b' }}>
            <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
              These employees have 0 Total Attendance AND 0 OT Total this month - left out of this pay run so
              nobody gets paid for 0 working days by accident. Correct their attendance and re-run Generate if
              they should be included:
            </p>
            <ul>
              {zeroAttendanceEmployees.map((entry, index) => (
                <li key={index}>{entry}</li>
              ))}
            </ul>
          </div>
        )}
        {missingBankAccountEmployees.length > 0 && (
          <div className="row-errors" style={{ background: '#fff8e1', borderColor: '#f0c14b' }}>
            <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
              Employees without Bank Account Details - these employees are still included in this pay run,
              but have no "pay to" bank account on file, so there's nowhere to actually transfer their
              salary to yet:
            </p>
            <ul>
              {missingBankAccountEmployees.map((entry, index) => (
                <li key={index}>{entry}</li>
              ))}
            </ul>
          </div>
        )}
        {reviewRequiredEmployees.length > 0 && (
          <div className="row-errors" style={{ background: '#fff8e1', borderColor: '#f0c14b' }}>
            <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
              Review Required - these employees were explicitly flagged "Review = Y" on the Attendance
              Report for this month. Verify their attendance before confirming payment:
            </p>
            <ul>
              {reviewRequiredEmployees.map((entry, index) => (
                <li key={index}>{entry}</li>
              ))}
            </ul>
          </div>
        )}
        {/* Shown once Generate has run - then always, regardless of any
            warnings or errors. If Generate produced no rows, it opens the
            Salary Statement's empty state rather than being unavailable. */}
        {hasGenerated && (
        <button
          type="button"
          className="view-report-button"
          onClick={() => {
            // Lets the checkboxes filter the already-generated data without
            // needing to re-run Generate - if one or more Pay Groups are
            // checked at the moment this is clicked, only those rows go to
            // the Salary Statement; if none are checked, everything does.
            const allRows = reportRows || []
            const rows = selectedGroups.length > 0
              ? allRows.filter((row) => selectedGroups.includes(row.group))
              : allRows
            navigate('/payroll/report', { state: { rows } })
          }}
        >
          View Salary Statement
        </button>
        )}
        {errorMessage && <p className="error-message">{errorMessage}</p>}

        {detailList.length > 0 && (
          <ul className="row-errors">
            {detailList.map((message, index) => (
              <li key={index}>{message}</li>
            ))}
          </ul>
        )}
      </div>
      </div>
    </>
  )
}

export default UploadSalaryExcel
